import { useEffect } from "react";
import VideoUIAccessError from "@platform/features/videoUI/components/VideoUIAccessError/VideoUIAccessError";
import VideoUIAlreadyInRoomError from "@platform/features/videoUI/components/VideoUIAlreadyInRoomError/VideoUIAlreadyInRoomError";
import VideoUILoading from "@platform/features/videoUI/components/VideoUILoading/VideoUILoading";
import VideoUIJoinContainer from "@platform/features/videoUI/components/VideoUIJoin/VideoUIJoinContainer";
import { useCustomMeetingManager } from "../hooks/useCustomMeetingManager";
import useRedirectWhenAnonymous from "@platform/hooks/useRedirectWhenAnonymous";
import { useDevices } from "../providers/DevicesProvider";
import {
  useVideo,
  useVideoDispatch,
  SET_INITIALIZED,
} from "../providers/VideoContext";
import { useMeetingManager } from "amazon-chime-sdk-component-library-react";
import { leaveMeeting } from "../utils/meeting/leaveMeeting";
import { leaveVideoRoom } from "../utils/api/leaveVideoRoom";
import useFetchUserCircles from "@platform/hooks/useFetchUserCircles";
import { useCurrentMeeting } from "@platform/hooks/useCurrentMeeting";

export function VideoJoinPage() {
  const {
    meetingId,
    meetingDetails,
    isFetchingMeetingDetails,
    meetingDetailsError,
    fetchMeetingDetails,
    verifyRoomAccess,
  } = useCustomMeetingManager();
  const { isDevicesLoading } = useDevices();
  const {
    isInitialized,
    roomId,
    roomAccessError,
    lastHeartbeat,
    isVerifyingRoomAccess,
    roomAccess,
  } = useVideo();
  const dispatch = useVideoDispatch();

  const meetingManager = useMeetingManager();

  const { currentMeeting } = useCurrentMeeting();

  useRedirectWhenAnonymous();

  const { isLoading: isLoadingCircle } = useFetchUserCircles({
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Clear any existing meeting session when joining page loads (similar to page refresh)
  useEffect(() => {
    const clearExistingSession = async () => {
      // If there's an active meeting session, disconnect from it
      if (isInitialized && meetingManager) {
        try {
          // Only disconnect from the Chime meeting session, don't clear meeting details
          // This mimics what happens on page refresh - the session is disconnected
          // but meeting details remain for the join page

          // Disconnect from the room if we have a roomId
          if (roomId) {
            try {
              await leaveVideoRoom({ room_id: roomId });
            } catch (apiError) {
              // Silently handle API errors
            }
          }

          // Disconnect from the Chime meeting if we have a meetingId
          if (meetingId) {
            await leaveMeeting({
              meetingId,
              meetingManager,
              setMeetingMode: () => {}, // No-op since we're not using meeting mode
            });
          }

          // Only clear the initialized state, keep meeting details
          dispatch({
            type: SET_INITIALIZED,
            payload: { isInitialized: false },
          });
        } catch (error) {
          // Silently handle errors during cleanup - we want to proceed with joining
          console.warn("Error clearing existing meeting session:", error);
          // Still clear the initialized state even if there's an error
          dispatch({
            type: SET_INITIALIZED,
            payload: { isInitialized: false },
          });
        }
      }
    };

    clearExistingSession();
  }, []); // Run once when component mounts

  useEffect(() => {
    if (!currentMeeting) return;
    if (isFetchingMeetingDetails) return;
    if (meetingId === currentMeeting.id.toString()) return;
    if (meetingDetails) return;

    fetchMeetingDetails(currentMeeting.id.toString());
  }, [
    currentMeeting,
    meetingId,
    isFetchingMeetingDetails,
    fetchMeetingDetails,
    meetingDetails,
  ]);

  // Verify room access when we have meeting details
  useEffect(() => {
    if (
      meetingDetails &&
      meetingDetails.video_rooms &&
      meetingDetails.video_rooms.length > 0 &&
      !roomAccess &&
      !roomAccessError
    ) {
      const videoRoom = meetingDetails.video_rooms[0];
      verifyRoomAccess(videoRoom.id);
    }
  }, [meetingDetails, roomAccess, roomAccessError, verifyRoomAccess]);

  // Show loading state
  if (isFetchingMeetingDetails || isVerifyingRoomAccess || isLoadingCircle) {
    return <VideoUILoading />;
  }

  // Show "already in room" error state
  if (roomAccessError === "ALREADY_IN_ROOM") {
    return <VideoUIAlreadyInRoomError lastHeartbeat={lastHeartbeat} />;
  }

  // Show other error state
  if (meetingDetailsError) {
    return <VideoUIAccessError errorMessage={meetingDetailsError} />;
  }

  // Show device setup for meeting details available AND room access has been verified successfully
  if (meetingDetails && roomAccess) {
    return (
      <VideoUIJoinContainer
        meetingStartTime={currentMeeting?.localStartTime}
        meetingDetails={meetingDetails}
        isDevicesLoading={isDevicesLoading}
      />
    );
  }

  return null;
}

export default VideoJoinPage;
